/**
 * Formatage des pourcentages — meme moteur et memes regles que `montant.ts`.
 *
 * Fichier separe parce qu'un taux n'est pas une somme : `montant.ts` porte une
 * devise, une regle de decimales fixes et une variante compacte pour les tableaux,
 * dont rien ne s'applique ici.
 *
 * C'est la LOCALE qui decide de l'espace avant le symbole (`fr-FR` en met une,
 * insecable — U+00A0, verifie), exactement comme `formaterDevise` laisse la locale
 * placer celle du `€`. Mais on ne peut pas utiliser `style: 'percent'` pour autant.
 *
 * ⚠️ POURQUOI PAS `style: 'percent'`. Il attend une FRACTION (`0.1` pour 10 %) alors
 * que les regles de commission stockent le taux sur 0-100 : il faudrait diviser par
 * 100, et cette division fait remonter le bruit du flottant des qu'on veut afficher
 * le taux sans l'arrondir — `1.1 / 100` rend « 1,1000000000000001 % » (verifie).
 * On formate donc le NOMBRE tel quel, et on lui ajoute le suffixe que la locale
 * aurait mis. Rien n'est code en dur, rien n'est divise.
 *
 * ⚠️ SANS ARRONDI. Une premiere version plafonnait a deux decimales : le champ de
 * saisie gardait `2,375` pendant que la phrase recapitulative, deux lignes plus bas,
 * annoncait « 2,38 % ». Exactement le defaut que ce ticket corrige — deux ecritures
 * du meme nombre sur le meme ecran. Un taux affiche doit etre le taux enregistre.
 *
 * Meme tiret que les montants sur une valeur absente : un taux inconnu et un taux
 * nul ne sont pas la meme information, et `0 %` sur une commission se lit comme
 * « pas de commission » alors que le champ peut simplement ne pas etre arrive.
 */
import { MONTANT_ABSENT } from './montant';

/**
 * Ce que `fr-FR` place apres le nombre dans un pourcentage — l'espace insecable et
 * le symbole. Derive de la locale une fois pour toutes plutot qu'ecrit `'\u00A0%'` :
 * la regle typographique reste celle d'`Intl`, comme pour le `€`.
 */
const SUFFIXE_POURCENTAGE = (() => {
  const parties = new Intl.NumberFormat('fr-FR', { style: 'percent' }).formatToParts(0.1);
  const apresLeNombre = parties.slice(parties.findIndex(partie => partie.type === 'integer') + 1);
  return apresLeNombre.map(partie => partie.value).join('');
})();

/**
 * `10 %` — le taux est attendu sur **0-100**, pas en fraction.
 *
 * Restitue le taux tel qu'il est stocke : `2,375` rend `2,375 %`, pas `2,38 %`.
 * `maximumFractionDigits: 20` est le plafond haut d'`Intl`, pas une precision
 * souhaitee : sans division, il rend la representation courte du nombre (`1.1` reste
 * `1,1`, verifie) et ne se declenche donc que sur des taux qui ont vraiment
 * ces decimales.
 */
export function formaterPourcentage(valeur: number | null | undefined): string {
  if (valeur === null || valeur === undefined || !Number.isFinite(valeur)) {
    return MONTANT_ABSENT;
  }
  return valeur.toLocaleString('fr-FR', { maximumFractionDigits: 20 }) + SUFFIXE_POURCENTAGE;
}
